import { PageStandard } from "../utils/PageStandard";
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    createFilterOptions,
    Divider,
    FormControl,
    FormLabel,
    Grid,
    Input,
    Option,
    Select,
    Stack,
    Switch,
    Textarea,
    Typography,
} from "@mui/joy";
import React, { useContext, useEffect, useState } from "react";
import { Experience, ExperienceStatus, ExperienceType, Organization, PermissionType } from "../utils/Model";
import moment from "moment";
import { ExperienceTags, fromDatetimeLocalInput, getUserTimezone, toDatetimeLocalInput } from "../utils/Utils";
import { useParams } from "react-router-dom";
import { editExperience, getExperience } from "../utils/ViewModel";
import { FeedbackContext } from "../utils/FeedbackContext";
import { RichTextEditor } from "../utils/RichTextEditor";

const filter = createFilterOptions<string>();

const emptyExperience: Experience = {
    allow_replay: false,
    created_at: "",
    created_by: "",
    description: "",
    end_datetime: null,
    group_max_size: null,
    group_min_size: null,
    group_required: false,
    auto_group_enabled: false,
    id: "",
    is_active: false,
    location_reference: "",
    max_participants: null,
    organization_id: "",
    price: 0,
    start_datetime: null,
    title: "",
    type: ExperienceType.event,
    img_url: "",
    emoji: null,
    tags: [],
    private_experience: false,
    status: ExperienceStatus.draft,
    chat_enabled: false,
    map_visible: false,
    ticket_visible: false,
    classifica_individuale_visible: false,
    classifica_gruppo_visible: false,
    code: null,
    token_to_participant: false,
    permissions: [],
    invite_email_subject: null,
    invite_email_body: null,
    invite_email_include_code: false,
};

export function EditGame() {
    const { id } = useParams<{ id: string }>();
    const { setMessage, setError } = useContext(FeedbackContext);
    const [info, setInfo] = useState<Experience>(emptyExperience);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            if (!id) return;
            const data = await getExperience(id);
            if (data) {
                setInfo({
                    ...data.experience,
                    // Held in local (naive) input-formatted form while editing; converted
                    // back to UTC ISO in handleSave.
                    start_datetime: toDatetimeLocalInput(data.experience.start_datetime) || null,
                    end_datetime: toDatetimeLocalInput(data.experience.end_datetime) || null,
                    tags: data.experience.tags ?? [],
                    permissions: data.experience.permissions ?? [],
                });
                setOrganizations(data.organizations);
            }
        };
        fetchData();
    }, [id]);

    const handleSave = async () => {
        setSaving(true);
        const success = await editExperience(id!, {
            ...info,
            start_datetime: fromDatetimeLocalInput(info.start_datetime ?? ""),
            end_datetime: fromDatetimeLocalInput(info.end_datetime ?? ""),
        });
        setSaving(false);
        if (success) setMessage("Esperienza aggiornata");
        else setError("Errore durante il salvataggio");
    };

    const set = <K extends keyof Experience>(key: K, value: Experience[K]) =>
        setInfo((prev) => ({ ...prev, [key]: value }));

    const canSave =
        info.title.length >= 1 &&
        info.location_reference.length >= 1 &&
        info.organization_id.length >= 1;

    return (
        <PageStandard
            title="Modifica esperienza"
            sidebarVisible={true}
            headerEnd={
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography fontFamily="monospace" level="body-xs" color="neutral">
                        {id}
                    </Typography>
                    <Chip size="sm" variant="soft" color={info.status === ExperienceStatus.public ? "success" : info.status === ExperienceStatus.draft ? "primary" : "danger"}>
                        {info.status}
                    </Chip>
                    <Button
                        variant="solid"
                        color="primary"
                        onClick={handleSave}
                        loading={saving}
                        disabled={!canSave}
                    >
                        Salva
                    </Button>
                </Stack>
            }
        >
            <Grid container spacing={3}>
                {/* ── Left col ── */}
                <Grid xs={12} md={6}>
                    <Stack spacing={2}>
                        <Typography level="title-sm" fontWeight="bold" textColor="text.tertiary" textTransform="uppercase" letterSpacing={0.5}>
                            Contenuto
                        </Typography>

                        <FormControl size="sm" error={info.title.length < 1}>
                            <FormLabel>Titolo</FormLabel>
                            <Input value={info.title} onChange={(e) => set("title", e.target.value)} />
                        </FormControl>

                        <FormControl size="sm">
                            <FormLabel>Descrizione</FormLabel>
                            <Textarea
                                value={info.description}
                                onChange={(e) => set("description", e.target.value)}
                                minRows={4}
                                maxRows={8}
                            />
                        </FormControl>

                        <Stack direction="row" spacing={2}>
                            <FormControl size="sm" sx={{ flex: 1 }}>
                                <FormLabel>Immagine di copertina (URL)</FormLabel>
                                <Input
                                    value={info.img_url}
                                    onChange={(e) => set("img_url", e.target.value)}
                                    placeholder="https://…"
                                />
                            </FormControl>
                            <FormControl size="sm" sx={{ width: 90 }}>
                                <FormLabel>Emoji</FormLabel>
                                <Input
                                    value={info.emoji ?? ""}
                                    onChange={(e) => set("emoji", e.target.value || null)}
                                    placeholder="🗺️"
                                />
                            </FormControl>
                        </Stack>

                        <FormControl size="sm">
                            <FormLabel>Tag</FormLabel>
                            <Autocomplete
                                multiple
                                placeholder="Tags"
                                value={info.tags}
                                onChange={(_, newValue) => set("tags", newValue)}
                                filterOptions={(options, params) => {
                                    const filtered = filter(options, params);
                                    const { inputValue } = params;
                                    if (inputValue !== "" && !options.includes(inputValue)) {
                                        filtered.push(inputValue);
                                    }
                                    return filtered;
                                }}
                                selectOnFocus
                                clearOnBlur
                                handleHomeEndKeys
                                freeSolo
                                options={ExperienceTags}
                            />
                        </FormControl>

                        <Stack direction="row" spacing={2}>
                            <FormControl size="sm" sx={{ flex: 1 }} error={info.location_reference === ""}>
                                <FormLabel>Luogo</FormLabel>
                                <Input
                                    value={info.location_reference}
                                    onChange={(e) => set("location_reference", e.target.value)}
                                    placeholder="Es. Torino"
                                />
                            </FormControl>
                            <FormControl size="sm" sx={{ width: 110 }}>
                                <FormLabel>Codice</FormLabel>
                                <Input
                                    value={info.code ?? ""}
                                    onChange={(e) => set("code", e.target.value.slice(0, 8) || null)}
                                    placeholder="ES01"
                                    slotProps={{ input: { maxLength: 8 } }}
                                />
                            </FormControl>
                        </Stack>
                    </Stack>
                </Grid>

                {/* ── Right col ── */}
                <Grid xs={12} md={6}>
                    <Stack spacing={2}>
                        <Typography level="title-sm" fontWeight="bold" textColor="text.tertiary" textTransform="uppercase" letterSpacing={0.5}>
                            Impostazioni
                        </Typography>

                        <Stack direction="row" spacing={2}>
                            <FormControl size="sm" sx={{ flex: 1 }} error={!Object.values(ExperienceType).includes(info.type)}>
                                <FormLabel>Tipologia</FormLabel>
                                <Select
                                    value={info.type}
                                    onChange={(_, v) => set("type", v!)}
                                >
                                    <Option value={ExperienceType.event}>Evento</Option>
                                    <Option value={ExperienceType.permanent}>Permanente</Option>
                                </Select>
                            </FormControl>
                            <FormControl size="sm" sx={{ flex: 1 }}>
                                <FormLabel>Stato</FormLabel>
                                <Select
                                    value={info.status}
                                    onChange={(_, v) => set("status", v!)}
                                >
                                    <Option value={ExperienceStatus.draft}>Bozza</Option>
                                    <Option value={ExperienceStatus.public}>Pubblica</Option>
                                    <Option value={ExperienceStatus.deleted}>Eliminata</Option>
                                </Select>
                            </FormControl>
                        </Stack>

                        {info.type === ExperienceType.event && (
                            <Stack direction="row" spacing={2}>
                                <FormControl size="sm" sx={{ flex: 1 }} error={!!info.start_datetime && !moment(info.start_datetime).isValid()}>
                                    <FormLabel>Inizio ({getUserTimezone()})</FormLabel>
                                    <Input
                                        type="datetime-local"
                                        value={info.start_datetime ?? ""}
                                        onChange={(e) => set("start_datetime", e.target.value || null)}
                                    />
                                </FormControl>
                                <FormControl
                                    size="sm"
                                    sx={{ flex: 1 }}
                                    error={!!info.end_datetime && (!moment(info.end_datetime).isValid() || moment(info.end_datetime).isSameOrBefore(moment(info.start_datetime)))}
                                >
                                    <FormLabel>Fine ({getUserTimezone()})</FormLabel>
                                    <Input
                                        type="datetime-local"
                                        value={info.end_datetime ?? ""}
                                        onChange={(e) => set("end_datetime", e.target.value || null)}
                                    />
                                </FormControl>
                            </Stack>
                        )}

                        <FormControl size="sm" error={info.organization_id === ""}>
                            <FormLabel>Organizzazione</FormLabel>
                            <Select
                                value={info.organization_id}
                                onChange={(_, v) => set("organization_id", v!)}
                            >
                                {organizations.map((org) => (
                                    <Option key={org.id} value={org.id}>{org.name}</Option>
                                ))}
                            </Select>
                        </FormControl>

                        <Stack direction="row" spacing={2}>
                            <FormControl size="sm" error={Number(info.max_participants) <= 0} sx={{ flex: 1 }}>
                                <FormLabel>Max partecipanti</FormLabel>
                                <Input
                                    type="number"
                                    value={info.max_participants ?? ""}
                                    onChange={(e) => set("max_participants", Number(e.target.value) || null)}
                                />
                            </FormControl>
                            <FormControl size="sm" error={Number(info.price) < 0} sx={{ flex: 1 }}>
                                <FormLabel>Prezzo (€/pers)</FormLabel>
                                <Input
                                    type="number"
                                    value={info.price}
                                    onChange={(e) => set("price", Number(e.target.value))}
                                />
                            </FormControl>
                        </Stack>

                        <FormControl size="sm">
                            <FormLabel>Gruppi richiesti</FormLabel>
                            <Select
                                value={Boolean(info.group_required)}
                                onChange={(_, v) => set("group_required", v!)}
                            >
                                <Option value={true}>Sì</Option>
                                <Option value={false}>No</Option>
                            </Select>
                        </FormControl>

                        {info.group_required && (
                            <Stack direction="row" spacing={2}>
                                <FormControl size="sm" sx={{ flex: 1 }}>
                                    <FormLabel>Autocompilazione gruppi</FormLabel>
                                    <Select
                                        value={Boolean(info.auto_group_enabled)}
                                        onChange={(_, v) => set("auto_group_enabled", v!)}
                                    >
                                        <Option value={true}>Sì</Option>
                                        <Option value={false}>No</Option>
                                    </Select>
                                </FormControl>
                                <FormControl size="sm" sx={{ flex: 1 }} error={Number(info.group_min_size) <= 0}>
                                    <FormLabel>Min per gruppo</FormLabel>
                                    <Input
                                        type="number"
                                        value={info.group_min_size ?? ""}
                                        onChange={(e) => set("group_min_size", Number(e.target.value) || null)}
                                    />
                                </FormControl>
                                <FormControl size="sm" sx={{ flex: 1 }} error={Number(info.group_max_size) < Number(info.group_min_size)}>
                                    <FormLabel>Max per gruppo</FormLabel>
                                    <Input
                                        type="number"
                                        value={info.group_max_size ?? ""}
                                        onChange={(e) => set("group_max_size", Number(e.target.value) || null)}
                                    />
                                </FormControl>
                            </Stack>
                        )}

                        <Divider />

                        <Typography level="title-sm" fontWeight="bold" textColor="text.tertiary" textTransform="uppercase" letterSpacing={0.5}>
                            Visibilità & funzionalità
                        </Typography>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 1.5,
                            }}
                        >
                            <BoolToggle label="Attiva" value={info.is_active} onChange={(v) => set("is_active", v)} />
                            <BoolToggle label="Privata" value={info.private_experience} onChange={(v) => set("private_experience", v)} />
                            <BoolToggle label="Visibile su mappa" value={info.map_visible} onChange={(v) => set("map_visible", v)} />
                            <BoolToggle label="Ticket visibile" value={info.ticket_visible} onChange={(v) => set("ticket_visible", v)} />
                            <BoolToggle label="Token ai partecipanti" value={info.token_to_participant} onChange={(v) => set("token_to_participant", v)} />
                            <BoolToggle label="Chat abilitata" value={info.chat_enabled} onChange={(v) => set("chat_enabled", v)} />
                            <BoolToggle label="Consenti replay" value={info.allow_replay} onChange={(v) => set("allow_replay", v)} />
                            <BoolToggle label="Classifica individuale visibile" value={info.classifica_individuale_visible} onChange={(v) => set("classifica_individuale_visible", v)} />
                            <BoolToggle label="Classifica di gruppo visibile" value={info.classifica_gruppo_visible} onChange={(v) => set("classifica_gruppo_visible", v)} />
                        </Box>

                        <FormControl size="sm">
                            <FormLabel>Permessi richiesti</FormLabel>
                            <Autocomplete
                                multiple
                                placeholder="Permessi"
                                value={info.permissions}
                                onChange={(_, newValue) => set("permissions", newValue)}
                                options={Object.values(PermissionType)}
                            />
                        </FormControl>

                        <Divider />

                        <Typography level="title-sm" fontWeight="bold" textColor="text.tertiary" textTransform="uppercase" letterSpacing={0.5}>
                            Email di invito
                        </Typography>

                        <FormControl size="sm">
                            <FormLabel>Oggetto</FormLabel>
                            <Input
                                placeholder={`Sei stato invitato a ${info.title || "..."}`}
                                value={info.invite_email_subject ?? ""}
                                onChange={(e) => set("invite_email_subject", e.target.value || null)}
                            />
                        </FormControl>

                        <FormControl size="sm">
                            <FormLabel>Testo del messaggio</FormLabel>
                            <RichTextEditor
                                placeholder="Ciao {{nome}}, sei stato invitato a partecipare all'esperienza {{esperienza}}..."
                                value={info.invite_email_body ?? ""}
                                onChange={(html) => set("invite_email_body", html || null)}
                            />
                            <Typography level="body-xs" textColor="text.tertiary" mt={0.5}>
                                Segnaposto disponibili: {"{{nome}}"}, {"{{cognome}}"}, {"{{esperienza}}"}.
                                Scrivi tu il saluto (es. "Ciao {"{{nome}}"},") — non viene aggiunto automaticamente.
                                Se lasciato vuoto viene usato un testo di default.
                                Link di accesso e, se creato ora, le credenziali dell'utente vengono
                                aggiunti automaticamente in fondo alla mail.
                            </Typography>
                        </FormControl>

                        <BoolToggle
                            label="Includi codice invito nella mail"
                            value={info.invite_email_include_code}
                            onChange={(v) => set("invite_email_include_code", v)}
                        />
                    </Stack>
                </Grid>
            </Grid>
        </PageStandard>
    );
}

function BoolToggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1.5,
                py: 1,
                borderRadius: "sm",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.level1",
            }}
        >
            <Typography level="body-sm">{label}</Typography>
            <Switch
                size="sm"
                checked={Boolean(value)}
                onChange={(e) => onChange(e.target.checked)}
            />
        </Box>
    );
}
